import { useEffect, useState, useCallback } from 'react';
import { ChevronRight, ChevronDown, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import type { CloseStatusResponse } from '../../types/shared';
import { fetchCloseStatus } from '../../api/inventory';
import OutstandingWarehouseRow from './OutstandingWarehouseRow';
import ApproveCloseModal from './ApproveCloseModal';
import ReopenCloseModal from './ReopenCloseModal';
import SubmissionDetailDrawer from './SubmissionDetailDrawer';

interface Props {
  regionId: string;
  period:   string; // 'YYYY-MM'
  /** Optional bump signal — caller increments when sibling state should refresh. */
  refreshKey?: number;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString([], { year: 'numeric', month: 'short', day: '2-digit' });
}

export default function RegionCloseCard({ regionId, period, refreshKey }: Props) {
  const [data, setData]         = useState<CloseStatusResponse | null>(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [bump, setBump]         = useState(0);
  const [showApprove, setShowApprove] = useState(false);
  const [showReopen,  setShowReopen]  = useState(false);
  const [drawerSubmissionId, setDrawerSubmissionId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await fetchCloseStatus(regionId, period);
      setData(d);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load close status.');
    } finally {
      setLoading(false);
    }
  }, [regionId, period]);

  useEffect(() => { load(); }, [load, refreshKey, bump]);

  const refresh = () => setBump(b => b + 1);

  // Header summary — render even while loading.
  return (
    <div className="rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-[#1e2521] overflow-hidden">
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-stone-50 dark:hover:bg-[#242d28] transition text-left"
      >
        <div className="flex items-center gap-3 min-w-0">
          {expanded
            ? <ChevronDown size={15} className="text-stone-400 shrink-0" />
            : <ChevronRight size={15} className="text-stone-400 shrink-0" />}
          <span className="font-semibold text-stone-900 dark:text-stone-100 truncate">
            {data?.region.name ?? '…'}
          </span>
          {data?.region.code && (
            <span className="text-xs text-stone-400 dark:text-stone-500">
              {data.region.code}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-sm shrink-0">
          {loading && <Loader2 size={14} className="text-stone-400 animate-spin" />}
          {data && <CloseStatusBadge data={data} />}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-stone-100 dark:border-stone-800 px-4 py-3 space-y-4">
          {error && (
            <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-3 py-2 text-sm text-red-700 dark:text-red-400">
              {error}
            </div>
          )}

          {data && !loading && (
            <>
              {/* Top action row */}
              <div className="flex items-center justify-between">
                <div className="text-xs text-stone-500 dark:text-stone-400">
                  {data.aggregate.countDateRange ? (
                    <>Counts taken {data.aggregate.countDateRange.earliest} – {data.aggregate.countDateRange.latest}</>
                  ) : (
                    <>No submissions yet for this period.</>
                  )}
                </div>
                <div className="flex gap-2">
                  {data.status === 'open' && (
                    <button
                      onClick={() => setShowApprove(true)}
                      className="px-3 py-1.5 text-sm rounded-lg bg-moss-600 text-white hover:bg-moss-700 transition"
                    >
                      Approve close
                    </button>
                  )}
                  {data.status === 'approved' && (
                    <button
                      onClick={() => setShowReopen(true)}
                      className="px-3 py-1.5 text-sm rounded-lg border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition"
                    >
                      Re-open
                    </button>
                  )}
                </div>
              </div>

              {/* Outstanding */}
              {data.warehouses.outstanding.length > 0 && (
                <section>
                  <h3 className="text-xs font-semibold text-stone-500 dark:text-stone-400 uppercase tracking-wide mb-2">
                    Outstanding ({data.warehouses.outstanding.length})
                  </h3>
                  <div className="space-y-1.5">
                    {data.warehouses.outstanding.map(w => (
                      <OutstandingWarehouseRow
                        key={w.warehouseId}
                        warehouse={w}
                        period={period}
                        onResolved={refresh}
                        disabled={data.status === 'approved'}
                      />
                    ))}
                  </div>
                </section>
              )}

              {/* Submitted */}
              {data.warehouses.submitted.length > 0 && (
                <section>
                  <h3 className="text-xs font-semibold text-stone-500 dark:text-stone-400 uppercase tracking-wide mb-2">
                    Submitted ({data.warehouses.submitted.length})
                  </h3>
                  <div className="space-y-1.5">
                    {data.warehouses.submitted.map(w => (
                      <button
                        key={w.warehouseId}
                        onClick={() => setDrawerSubmissionId(w.submissionId)}
                        className="w-full flex items-center justify-between px-3 py-2 rounded-lg border border-stone-100 dark:border-stone-800 hover:bg-stone-50 dark:hover:bg-[#242d28] transition text-left"
                      >
                        <div className="flex items-center gap-2 text-sm min-w-0">
                          <span
                            className={`text-[10px] font-medium uppercase tracking-wide rounded px-1.5 py-0.5 shrink-0 ${
                              w.type === 'hub'
                                ? 'bg-moss-100 text-moss-700 dark:bg-moss-900/30 dark:text-moss-300'
                                : 'bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-400'
                            }`}
                          >
                            {w.type}
                          </span>
                          <span className="text-stone-800 dark:text-stone-200 truncate">{w.name}</span>
                          {w.isEmpty && (
                            <span className="text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 shrink-0">
                              empty
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-stone-500 dark:text-stone-400 shrink-0">
                          <span>{w.submittedBy.name}</span>
                          <span>·</span>
                          <span>{formatDate(w.inventoryDate)}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </section>
              )}

              {/* Aggregate */}
              {data.aggregate.items.length > 0 && (
                <section>
                  <h3 className="text-xs font-semibold text-stone-500 dark:text-stone-400 uppercase tracking-wide mb-2">
                    Regional aggregate
                  </h3>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-stone-500 dark:text-stone-400 border-b border-stone-200 dark:border-stone-700">
                        <th className="text-left py-1.5 pr-2 font-medium">Item</th>
                        <th className="text-left py-1.5 pr-2 font-medium">Category</th>
                        <th className="text-right py-1.5 pr-2 font-medium">Total</th>
                        <th className="text-right py-1.5 font-medium">Warehouses</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.aggregate.items.map(i => (
                        <tr key={i.inventoryItemId} className="border-b border-stone-100 dark:border-stone-800 last:border-0">
                          <td className="py-1.5 pr-2 text-stone-800 dark:text-stone-200">{i.name}</td>
                          <td className="py-1.5 pr-2 text-xs text-stone-500 dark:text-stone-400">{i.category}</td>
                          <td className="py-1.5 pr-2 text-right tabular-nums text-stone-800 dark:text-stone-200">{i.totalQuantity.toLocaleString()}</td>
                          <td className="py-1.5 text-right text-xs text-stone-500 dark:text-stone-400">{i.byWarehouse.length}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              )}
            </>
          )}
        </div>
      )}

      {showApprove && data && (
        <ApproveCloseModal
          regionId={regionId}
          regionName={data.region.name}
          period={period}
          outstandingCount={data.warehouses.outstanding.length}
          onClose={() => setShowApprove(false)}
          onApproved={() => { setShowApprove(false); refresh(); }}
        />
      )}
      {showReopen && data && (
        <ReopenCloseModal
          regionId={regionId}
          regionName={data.region.name}
          period={period}
          onClose={() => setShowReopen(false)}
          onReopened={() => { setShowReopen(false); refresh(); }}
        />
      )}
      {drawerSubmissionId && (
        <SubmissionDetailDrawer
          submissionId={drawerSubmissionId}
          onClose={() => setDrawerSubmissionId(null)}
        />
      )}
    </div>
  );
}

function CloseStatusBadge({ data }: { data: CloseStatusResponse }) {
  if (data.status === 'approved' && data.approval) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full bg-moss-100 text-moss-700 dark:bg-moss-900/30 dark:text-moss-300">
        <CheckCircle2 size={12} />
        Approved by {data.approval.approvedBy.name} on {formatDate(data.approval.approvedAt)}
      </span>
    );
  }
  const out  = data.warehouses.outstanding.length;
  const subm = data.warehouses.submitted.length;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
      <AlertCircle size={12} />
      Open · {subm} submitted, {out} outstanding
    </span>
  );
}
